import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConversationReadView = ({ conversationId, onBack, onContinue }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [messages, setMessages] = useState([]);
  const [conversationTitle, setConversationTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    // Auto scroll to bottom smoothly after messages load
    if (messages.length > 0 && !isLoading) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [messages, isLoading]);

  const loadConversation = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setConversationTitle(response.data.title || 'Conversation');
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setIsLoading(false);
    }
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    
    return date.toLocaleDateString([], {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  // Group messages by day
  const getDateForMessage = (dateStr) => {
    const date = new Date(dateStr);
    return date.toDateString();
  };

  const renderMessages = () => {
    let lastDate = null;
    const elements = [];

    messages.forEach((message, index) => {
      const messageDate = getDateForMessage(message.created_at);
      
      // Add date header if it's a new day
      if (messageDate !== lastDate) {
        elements.push(
          <div
            key={`date-${index}`}
            style={{
              textAlign: 'center',
              padding: '32px 0',
            }}
          >
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '12px',
                fontWeight: 400,
                color: 'rgba(255, 243, 225, 0.2)',
                letterSpacing: '0.05em'
              }}
            >
              {formatDateHeader(message.created_at)}
            </span>
          </div>
        );
        lastDate = messageDate;
      }

      // Add message
      if (message.role === 'user') {
        elements.push(
          <div
            key={`msg-${index}`}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '24px'
            }}
          >
            <div
              style={{
                background: 'rgba(255, 243, 225, 0.04)',
                borderRadius: '12px 12px 2px 12px',
                padding: '16px 20px',
                maxWidth: '85%'
              }}
            >
              <p
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '19px',
                  fontWeight: 400,
                  lineHeight: 2.0,
                  color: 'rgba(255, 243, 225, 0.88)',
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {message.content}
              </p>
            </div>
          </div>
        );
      } else {
        elements.push(
          <div
            key={`msg-${index}`}
            style={{
              marginBottom: '24px'
            }}
          >
            <div
              style={{
                borderLeft: '2px solid rgba(196, 137, 74, 0.25)',
                paddingLeft: '16px'
              }}
            >
              <p
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: '21px',
                  fontWeight: 300,
                  fontStyle: 'italic',
                  lineHeight: 2.1,
                  color: 'rgba(255, 243, 225, 0.82)',
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {message.content}
              </p>
            </div>
          </div>
        );
      }
    });

    return elements;
  };

  return (
    <div
      data-testid="conversation-read-view"
      style={{
        position: 'fixed',
        inset: 0,
        background: '#080608',
        backgroundImage: 'radial-gradient(ellipse at 50% 40%, rgba(62, 28, 18, 0.4) 0%, rgba(8, 6, 8, 0) 70%)',
        zIndex: 50,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.8s ease-out',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Velvet texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.035,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }}
      />
      
      {/* Vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)'
        }}
      />

      {/* Header with title */}
      <div style={{
        padding: '48px 32px 24px 32px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <h1
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: '22px',
            fontWeight: 300,
            fontStyle: 'italic',
            color: 'rgba(255, 243, 225, 0.6)',
            margin: 0
          }}
        >
          {conversationTitle}
        </h1>
      </div>

      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '0 32px 120px 32px',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'rgba(196, 137, 74, 0.6)',
                  margin: '0 auto',
                  animation: 'pulse 2s ease-in-out infinite'
                }}
              />
            </div>
          ) : (
            renderMessages()
          )}
          <div ref={messagesEndRef} style={{ height: '1px' }} />
        </div>
      </div>

      {/* Fixed bottom bar */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10
        }}
      >
        {/* Gradient fade */}
        <div
          style={{
            height: '40px',
            background: 'linear-gradient(to bottom, transparent 0%, #080608 100%)',
            pointerEvents: 'none'
          }}
        />
        
        {/* Bar content */}
        <div
          style={{
            height: '56px',
            background: '#080608',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 32px'
          }}
        >
          <button
            onClick={onBack}
            data-testid="back-to-sessions"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255, 243, 225, 0.55)',
              padding: '12px 0',
              transition: 'color 0.2s ease'
            }}
          >
            ← back
          </button>
          
          <button
            onClick={onContinue}
            data-testid="continue-conversation"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255, 243, 225, 0.55)',
              padding: '12px 0',
              transition: 'color 0.2s ease'
            }}
          >
            continue here →
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default ConversationReadView;
